import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/adminApi';

const AdminAuthContext = createContext(null);

// Allowed admin email domains and special emails
const ALLOWED_DOMAINS = ['botibonga.com'];
const ALLOWED_EMAILS = ['dennismthairu@gmail.com'];

export function isAdminEmail(email) {
  if (!email) return false;
  const emailLower = email.toLowerCase();

  // Check special allowed emails
  if (ALLOWED_EMAILS.includes(emailLower)) {
    return true;
  }

  // Check allowed domains
  return ALLOWED_DOMAINS.some(domain => emailLower.endsWith(`@${domain}`));
}

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.getMe();
      if (response.admin) {
        setAdmin(response.admin);
      }
    } catch (err) {
      // Not authenticated, that's fine
      setAdmin(null);
    } finally {
      setIsLoading(false);
    }
  };

  const signInWithGoogle = useCallback(async (googleResponse) => {
    try {
      setIsLoading(true);
      setError(null);

      // Extract credential from Google response
      const { credential } = googleResponse;

      // Decode the JWT to get email (for client-side validation)
      const payload = JSON.parse(atob(credential.split('.')[1]));
      const email = payload.email;

      // Client-side check (server will also validate)
      if (!isAdminEmail(email)) {
        setError('Access denied. Only @botibonga.com emails or authorized admins can access this panel.');
        setIsLoading(false);
        return { success: false, error: 'Access denied' };
      }

      // Send to backend for validation and session creation
      const response = await adminApi.googleAuth(credential);

      if (response.success && response.admin) {
        setAdmin(response.admin);
        return { success: true };
      } else {
        setError(response.message || 'Authentication failed');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const message = err.message || 'Authentication failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await adminApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setAdmin(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    admin,
    isLoading,
    isAuthenticated: !!admin,
    error,
    signInWithGoogle,
    signOut,
    clearError,
    checkAuthStatus,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}

export default AdminAuthContext;
